import {
	ContractDamageDescriptions,
	ContractHazardDescriptions,
	capitalize,
	type Dtos,
	formatToDollar,
	pluralize,
	type SfaaCodeId,
	type SuretyAccountId,
	type SuretyBondId,
	SuretyType,
} from "@inrev/common";
import { type AdminSuretyBond, useAdminUpdateBond } from "@portal/api/bond/admin";
import { useFetchRecentlyUsedSfaaCodes, useFetchSfaaCodes } from "@portal/api/sfaa-code/admin";
import { isSfaaContractObligee } from "@portal/components/features/sfaa-code/sfaaCodeFilters";
import { SfaaCodeSelectInput } from "@portal/components/form/inputs/SfaaCodeSelectInput";
import { SideAnchoredTextBlock } from "@portal/components/patterns/side-anchored-text";
import { Card, Text } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useEffect, useState } from "react";
import { AdminDataList, type AdminDataListProps } from "../../shared/admin/AdminDataList";
import { NAICSCodesList } from "./NAICSCodeList";

type AdminBondProjectFields =
	| "id"
	| "accountId"
	| "suretyType"
	| "sfaaCode"
	| "project"
	| "contractSuretyType";

type AdminBondProjectBond =
	| Pick<
			Extract<AdminSuretyBond, { contractSuretyType: "bid" }>,
			AdminBondProjectFields | "bidAmount"
	  >
	| Pick<
			Extract<AdminSuretyBond, { contractSuretyType: "final" }>,
			AdminBondProjectFields | "contractAmount"
	  >;

export type AdminBondProjectCardProps = {
	bond: AdminBondProjectBond;
};

const describedLines = (lines: readonly string[]) =>
	lines.length > 0 ? (
		<div className="flex w-fit flex-col space-y-[2px] text-right">
			{lines.map((line) => (
				<Text key={line} type="caption" color="primary">
					{capitalize(line)}
				</Text>
			))}
		</div>
	) : null;

const ProjectDescription = ({
	projectName,
	projectDescription,
}: {
	projectName?: string | null;
	projectDescription: string | null | undefined;
}) => (
	<div className="flex min-w-0 flex-col gap-2 px-[10px] py-[14px]">
		{projectName !== undefined && (
			<Text
				type="body-strong"
				color="primary"
				className="flex break-words"
				data-testid="admin-bond-project-name"
			>
				{projectName ?? "No project name provided"}
			</Text>
		)}
		<SideAnchoredTextBlock text={projectDescription ?? "No project description provided"} />
	</div>
);

const SfaaCodeRow = ({
	bondId,
	accountId,
	sfaaCode,
	suretyType,
}: {
	bondId: SuretyBondId;
	accountId: SuretyAccountId;
	sfaaCode: Dtos.SfaaCode.Get.Response | null;
	suretyType: SuretyType;
}) => {
	const savedId = sfaaCode?.id ?? null;
	const [selectedId, setSelectedId] = useState<SfaaCodeId | null>(savedId);
	const { updateBond, updateBondIsLoading } = useAdminUpdateBond(bondId, accountId);
	const { sfaaCodes, sfaaCodesError, sfaaCodesIsLoading } = useFetchSfaaCodes();
	const { recentlyUsedSfaaCodes } = useFetchRecentlyUsedSfaaCodes(accountId, { suretyType });
	const initialOwnerType =
		sfaaCode && isSfaaContractObligee(sfaaCode.obligeeType) ? sfaaCode.obligeeType : undefined;

	useEffect(() => {
		setSelectedId(savedId);
	}, [savedId]);

	const selectSfaaCode = (id: SfaaCodeId | undefined) => {
		const nextId = id ?? null;
		if (nextId === savedId || updateBondIsLoading) return;
		setSelectedId(nextId);
		updateBond({ sfaaCodeId: nextId }, { onError: () => setSelectedId(savedId) });
	};

	return (
		<div className="flex w-full max-w-[420px] flex-col gap-1" data-testid="admin-bond-sfaa-code">
			<SfaaCodeSelectInput
				sfaaCodes={sfaaCodes}
				recentlyUsed={recentlyUsedSfaaCodes}
				isLoading={sfaaCodesIsLoading}
				suretyType={suretyType}
				initialOwnerType={initialOwnerType}
				value={selectedId ?? undefined}
				onChange={selectSfaaCode}
				disabled={updateBondIsLoading}
				compact
			/>
			{sfaaCodesError ? (
				<Text type="caption" color="danger">
					Couldn't load SFAA codes. Try again.
				</Text>
			) : null}
		</div>
	);
};

export const AdminBondProjectCard = ({ bond }: AdminBondProjectCardProps) => {
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const project = bond.project.input;
	const rows: AdminDataListProps["data"] = [];

	if (bond.contractSuretyType === "bid") {
		rows.push({ label: "Bid Amount", value: formatToDollar(bond.bidAmount) });
	}
	if (bond.contractSuretyType === "final") {
		rows.push({ label: "Contract Amount", value: formatToDollar(bond.contractAmount) });
	}
	rows.push(
		{
			label: "Location",
			value: project.address ? `${project.address.city}, ${project.address.state}` : "Unknown",
		},
		{
			label: "Time to Complete",
			value: pluralize(project.monthsToComplete, "month", "months"),
		},
		{
			label: "Warranty Period",
			value: pluralize(project.contractWarrantyMonths, "month", "months"),
		},
		{
			label: "Scope of Work",
			value: (
				<NAICSCodesList
					naicsCodes={project.scopeOfWork}
					showRatio
					className="min-w-[400px] max-w-[400px]"
				/>
			),
		},
		{
			label: "SFAA Code",
			value: (
				<SfaaCodeRow
					bondId={bond.id}
					accountId={bond.accountId}
					sfaaCode={bond.sfaaCode}
					suretyType={bond.suretyType}
				/>
			),
		},
		{
			label: "Contract Hazards",
			value: describedLines(
				(project.contractHazards ?? []).map((hazard) => ContractHazardDescriptions[hazard]),
			),
		},
		{
			label: "Contract Damages",
			value: describedLines(
				(project.contractDamages ?? []).map((damage) => ContractDamageDescriptions[damage]),
			),
		},
	);

	return (
		<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-project-card">
			<Card.Body className="p-6">
				<div className="flex w-full flex-col space-y-5">
					<Text type="title" color="primary">
						Project Details
					</Text>
					<div className="flex flex-col divide-y divide-bc-default">
						<ProjectDescription
							projectName={isMinimalUIEnabled ? project.name : undefined}
							projectDescription={project.description}
						/>
						<AdminDataList data={rows} bare />
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
